import { FC } from 'react';
import { Label } from 'src/components/Label';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Option } from 'src/components/SelectBox';
import { CommunicationTiming } from 'src/types/automation';
import StepCard from '../step-card/StepCard';
import type { StepMetrics } from '../step-card/StepCardHeader';
import { Robot, CalendarBlank } from '@phosphor-icons/react';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import StepTimingControls from '../step-card/StepTimingControls';

type CallStepCardProps = {
  stepNumber: number;
  required: boolean;
  onAgentChange: (option: Option) => void;
  fetchAgentOptions: (name: string) => Promise<Option[]>;
  agentOptions: Option[];
  loadingAgents: boolean;
  communicationTiming?: CommunicationTiming;
  onCommunicationTimingChange?: (timing: CommunicationTiming) => void;
  timeWindowOptions?: Option[];
  onTimeWindowChange?: (option: Option) => void;
  selectedAgentOption?: Option;
  onTitleChange?: (value: string) => void;
  selectedTimeWindowOption?: Option;
  metrics?: StepMetrics;
  addStepAbove?: () => void;
  addStepBelow?: () => void;
  deleteStep?: () => void;
  hideTimeWindow?: boolean;
  disabled?: boolean;
};

export const CallStepCard: FC<CallStepCardProps> = ({
  stepNumber,
  required,
  selectedAgentOption,
  onAgentChange,
  onTitleChange,
  communicationTiming,
  onCommunicationTimingChange,
  timeWindowOptions,
  selectedTimeWindowOption,
  onTimeWindowChange,
  fetchAgentOptions,
  agentOptions,
  loadingAgents,
  metrics,
  addStepAbove,
  addStepBelow,
  deleteStep,
  hideTimeWindow = false,
  disabled = false,
}) => {
  const isError = Boolean(required && !selectedAgentOption);

  const getError = () => {
    if (!selectedAgentOption) {
      return 'Agent still needs to be assigned';
    }
    return '';
  };

  const summary = (
    <div className="flex flex-wrap items-center gap-6">
      {isError && getError() ? (
        <div className="inline-flex items-center gap-2 py-2 px-3 rounded-xl bg-error/10">
          <Robot className="h-5 w-5" />
          <p>{getError()}</p>
        </div>
      ) : (
        <div className="inline-flex items-center gap-2">
          <Robot className="h-5 w-5 text-neutral-500" />
          <p className="text-neutral-700">
            {selectedAgentOption?.label || '-'}
          </p>
        </div>
      )}
      <div className="inline-flex items-center gap-2">
        <CalendarBlank className="h-5 w-5 text-neutral-500" />
        <p className="text-neutral-700 leading-normal">
          {communicationTiming === 'earliest'
            ? 'At the earliest eligible time'
            : selectedTimeWindowOption?.label}
        </p>
      </div>
    </div>
  );

  return (
    <StepCard
      stepNumber={stepNumber}
      titleDefaultValue={{
        value: CommunicationChannel.CALL,
        label: 'Phone Call',
      }}
      titleOnChange={onTitleChange}
      metrics={metrics}
      summary={summary}
      error={isError}
      onAddStepAbove={addStepAbove}
      onAddStepBelow={addStepBelow}
      onDeleteStep={deleteStep}
      disabled={disabled}
    >
      <div className="flex flex-col gap-y-6">
        <div className="w-full">
          <Label required={required} htmlFor="call-step-agent">
            Agent {disabled ? '(Disabled)' : ''}
          </Label>
          <AutoCompleteSelectBox
            defaultValue={selectedAgentOption}
            fetchOptions={fetchAgentOptions}
            onChange={(value) => {
              const option = agentOptions.find((item) => item.value === value)!;
              onAgentChange(option);
            }}
            variant="outlined"
            color="primary"
            size="medium"
            disabled={loadingAgents || disabled}
            className="w-full max-w-[33rem]"
          />
        </div>
        {!hideTimeWindow && (
          <StepTimingControls
            label="When to place call"
            timing={communicationTiming}
            onTimingChange={onCommunicationTimingChange}
            timeWindowOptions={timeWindowOptions}
            selectedTimeWindowOption={selectedTimeWindowOption}
            onTimeWindowChange={onTimeWindowChange}
            disabled={disabled}
          />
        )}
      </div>
    </StepCard>
  );
};

export default CallStepCard;
